import "../es.scss";

import Alert from "./alert";
import API from "./api";
import Logger from "./logger";
import BaseMapLegend from "./control/BaseMapLegend";
import DirectionsModel from "./map/DirectionsModel";
import ErrorControl from "./control/ErrorControl";
import FilterControl from "./control/filter/FilterControl";
import FilterControlUtil from "./control/filter/FilterControlUtil";
import TemporaryPointControl from "./control/TemporaryPointControl";
import UserSettingsControl from "./control/UserSettingsControl";
import SFDCClient from "./util/sfdcClient";
import Util from "./util/util";

const $ = global.$;
const _ = global._;

const Intl = global.Intl || undefined;

/**
 * @param es we extend this var incase some of the vals were already initialised.
 * e.g. by an embedded jsp or some other application js file loaded before our API.
 */
// TODO: Convert to class and initialize all here... that means preload has to be refactored so it doesn't inject in the es itself!
export default function initEs(es) {
    "use strict";

    // resolve it when the api has finished loading so people can use commands like es.load.then(fn)
    es.load = es.load || new $.Deferred();

    // if set to true then the api will not handle server response errors itself but will just let it pass to each api calls native deferred handlers (.fail(), .always())
    es.suppressDefaultErrorHandling = false;

    // for inter-tab communication e.g. bc.postMessage("es123."); ... channel.onmessage = (e) => { console.log('Received', e.data); }
    es.bc = new BroadcastChannel("eS");
    es.bc.onmessage = (e) => {
        console.log("Default onmessage handler", e);
    };

    es.Intl = Intl;

    // TODO: Initialize them here instead of injected somewhere else...
    es.module = {};
    es.routing = {};
    es.common = {};

    es.control = {
        BaseMapLegend: BaseMapLegend,
        Directions: DirectionsModel,
        TemporaryPoint: TemporaryPointControl,
        FilterControl: FilterControl,
        
        filterUtil: new FilterControlUtil(),
        userSettings: new UserSettingsControl()
    };

    es.log = new Logger();
    es.alert = new Alert();
    es.util = new Util();
    es.error = new ErrorControl(); // es.util must be initialized before ErrorControl is initialized
    es.api = new API();

    // only load it after es.util is defined (as there's static props that use it)
    // as it's loaded using require instead of import, must use default getter...
    const MapModule = require("./map").default;
    es.map = new MapModule();

    if (es.isEmbeddedInSFDC) {
        const signedRequestPromise = es.api.integrationSalesforce.getSignedCanvasRequestJson();
        signedRequestPromise.fail((err) => {
            es.log.warn('getSignedCanvasRequestJson failed', err);
            renderSfdcAuthFailureUi('(unknown user)');
        });
        signedRequestPromise.done((signedCanvasRequestJson) => {
            // PKCE OAuth-flow override: Salesforce-issued Canvas signed_request oauthToken is
            // invalid for REST on Connected Apps with PKCE enforcement.
            // We have to obtain a separate, valid token via a server PKCE flow (see salesforceOAUTH.jsp +
            // IntegrationSalesforceResource#pkceStart / #pkceExchange / #pkceToken) which stores the token server-side.
            const sfUserId = signedCanvasRequestJson?.context?.user?.userId;

            fetchPkceTokenFromServer(sfUserId).then((pkceToken) => {
                if (!pkceToken) {
                    triggerPkceReauth(signedCanvasRequestJson);
                    return;
                }
                // We made it past the validity check - clear the retry counter so future
                // failures start fresh.
                try {
                    sessionStorage.removeItem('es_sfdc_pkce_retry_count');
                } catch (ignore) { /* best effort */ }

                try {
                    es.log.warn('PKCE OVERRIDE: APPLYING OVERRIDE. Previous token suffix was ...' + (signedCanvasRequestJson.client?.oauthToken ? String(signedCanvasRequestJson.client.oauthToken).slice(-12) : '(none)') + ' new suffix ...' + String(pkceToken.access_token).slice(-12));
                    signedCanvasRequestJson.client.oauthToken = pkceToken.access_token;

                    if (pkceToken.instance_url && signedCanvasRequestJson.context && signedCanvasRequestJson.context.links) {
                        signedCanvasRequestJson.client.instanceUrl = pkceToken.instance_url;
                        // Keep links as relative paths (the shape Canvas originally provides). The
                        // host is carried via client.instanceUrl above. Absolute URLs here break
                        // composite/batch subrequests, which require relative `url` values.
                        const apiVersion = es.salesforceSettings.apiVersion; // e.g. "v64.0"
                        const restUrl = '/services/data/' + apiVersion + '/';
                        signedCanvasRequestJson.context.links.restUrl = restUrl;
                        signedCanvasRequestJson.context.links.sobjectUrl = restUrl + 'sobjects/';
                        signedCanvasRequestJson.context.links.queryUrl = restUrl + 'query/';
                    }
                } catch (e) {
                    es.log.warn('es.ts: PKCE override apply failed', e);
                }

                es.SFDC = {
                    signedRequest: replaceAPIVersion(signedCanvasRequestJson)
                };
                es.SFDC.client = new SFDCClient(es.SFDC.signedRequest, Sfdc.canvas.client);
                es.load.resolve();
            });
        });
    } else {
        es.load.resolve();
    }

    es.salesforceSettings = es.salesforceSettings || {};
    es.azureMapsKey = es.azureMapsKey || "n/a";
    es.azureMapsAdvancedKey = es.azureMapsAdvancedKey || "n/a";
    es.mapboxAccessToken = es.mapboxAccessToken || "n/a";
    es.mapboxGeocoderPermanent = es.mapboxGeocoderPermanent || false;

    es.isNativeApp = es.isNativeApp || false;
    es.isTerritoryComparePage = es.isTerritoryComparePage || false;
}

interface PkceTokenResponse {
    access_token: string;
    instance_url: string;
    sf_user_id: string;
    expires_at_ms: number;
}

/**
 * Fetches a live PKCE access_token for the given SF userId from eS rest endpoint. Returns
 * null if the server has no stored token (in which case caller should kick off the auth flow). The server
 * handles refresh-token rotation behind this call, so returned token is always live or the rs is 404.
 */
function fetchPkceTokenFromServer(sfUserId: string | null | undefined): Promise<PkceTokenResponse | null> {
    return new Promise((resolve) => {
        if (!sfUserId) {
            resolve(null);
            return;
        }
        const url = es.restUrl + '/integration_salesforce/pkce_token?sfUserId=' + encodeURIComponent(sfUserId);
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) {
                return;
            }
            if (xhr.status === 200) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch (e) {
                    es.log.warn('PKCE token fetch: parse failed', e);
                    resolve(null);
                }
            } else if (xhr.status === 404) {
                es.log.warn('PKCE token fetch MISS - server has no token for sfUserId=' + sfUserId);
                resolve(null);
            } else {
                es.log.warn('PKCE token fetch unexpected status=' + xhr.status + ': ' + xhr.responseText);
                resolve(null);
            }
        };
        xhr.send();
    });
}

/**
 * Replaces the iframe body with a stable error page. Used when PKCE auth has failed and
 * we don't want the rest of the app to load (it hits "Cannot read properties of undefined" and all sorts errors)
 *
 * @param sfUserName  SF username for display
 * @param detail      technical reason shown below the main message (visible to devs / support)
 */
function renderSfdcAuthFailureUi(sfUserName: string, detail: string): void {
    // document.body can be null if this fires before the DOM is parsed (e.g. es.ts running during a
    // canvas re-init / mid-navigation). Defer until the body exists rather than throwing
    // "Cannot set properties of null (setting 'innerHTML')" and leaving the user on a blank page.
    if (!document.body) {
        document.addEventListener('DOMContentLoaded', () => renderSfdcAuthFailureUi(sfUserName, detail), { once: true });
        return;
    }
    // Strip whatever was rendered and show a single message.
    const esc = (s: string) => String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    document.body.innerHTML =
        '<div style="max-width:560px; margin:80px auto; padding:24px; font-family:-apple-system,BlinkMacSystemFont,sans-serif; color:#222;">' +
            '<h1 style="font-size:20px; margin:0 0 16px;">Could not sign in to Salesforce</h1>' +
            '<p style="line-height:1.5;">eSpatial was unable to obtain a Salesforce access token for <b>' + esc(sfUserName) + '</b>.</p>' +
            '<p style="line-height:1.5; color:#555; font-size:13px; background:#f5f5f5; padding:10px; border-radius:4px;">' + esc(detail) + '</p>' +
            '<p><button onclick="window.location.reload()" style="padding:8px 16px; font-size:14px; cursor:pointer; ' +
            'border:1px solid #0070d2; background:#0070d2; color:white; border-radius:4px;">Reload</button></p>' +
        '</div>';
}

/**
 * Redirect the iframe to the auto-trigger OAuth page to obtain a fresh PKCE token. Includes a
 * circuit-breaker so repeated misses don't infinite-loop: after 2 attempts in one session we
 * show an error and stop redirecting (typical cause: user has multiple SF sessions and SF keeps
 * silently picking the wrong one despite login_hint).
 */
function triggerPkceReauth(signedCanvasRequestJson: any): void {
    const sfUserId = signedCanvasRequestJson?.context?.user?.userId;
    const RETRY_FLAG = 'es_sfdc_pkce_retry_count';
    const retryCount = parseInt(sessionStorage.getItem(RETRY_FLAG) || '0', 10) + 1;
    if (retryCount > 2) {
        sessionStorage.removeItem(RETRY_FLAG);
        const msg = 'PKCE OVERRIDE: giving up after ' + (retryCount - 1) + ' redirect attempts. SF userId expected=' + sfUserId +
            '. If you just restarted the server, click Reload to try again.';
        es.log.warn(msg);
        const userName = signedCanvasRequestJson?.context?.user?.userName || '(unknown)';
        // Replace the iframe contents with a stable error page rather than letting the app continue to load
        renderSfdcAuthFailureUi(userName,
            'Auth flow did not complete after ' + (retryCount - 1) + ' attempt(s). ' +
            'If you just restarted the server, click Reload. ' +
            'Otherwise, check that you are signed into Salesforce as ' + sfUserId + ' in this browser.');
        // Deliberately NOT calling es.load.resolve() - we don't want the rest of the app to init (will break anyway)
        return;
    }
    sessionStorage.setItem(RETRY_FLAG, String(retryCount));

    es.log.warn('PKCE OVERRIDE: no valid PKCE token for SF userId=' + sfUserId + ' - starting flow and redirecting (attempt ' + retryCount + ')');

    startServerPkceFlowAndRedirect(signedCanvasRequestJson);
}

/**
 * Calls the authenticated /rest/v1b/integration_salesforce/pkce_start endpoint to begin a flow,
 * stashes the resulting flow_id + code_challenge in sessionStorage so mini-sfdc-client.js
 * can pick them up after the redirect, then redirects the iframe to salesforceOAUTH.jsp.
 */
function startServerPkceFlowAndRedirect(signedCanvasRequestJson: any): void {
    const sfLoginUrl = signedCanvasRequestJson?.context?.links?.loginUrl;
    const sfUserName = signedCanvasRequestJson?.context?.user?.userName;

    const xhr = new XMLHttpRequest();
    xhr.open('GET', es.restUrl + '/integration_salesforce/pkce_start', true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) {
            return;
        }
        if (xhr.status !== 200) {
            const detail = 'pkce_start returned HTTP ' + xhr.status + ': ' + (xhr.responseText || '(empty response)');
            es.log.warn('[PKCE] ' + detail);
            renderSfdcAuthFailureUi(sfUserName || '(unknown)', detail);
            return;
        }
        let body;
        try {
            body = JSON.parse(xhr.responseText);
        } catch (e) {
            const detail = 'pkce_start returned non-JSON response: ' + xhr.responseText;
            es.log.warn('[PKCE] ' + detail);
            renderSfdcAuthFailureUi(sfUserName || '(unknown)', detail);
            return;
        }
        try {
            sessionStorage.setItem('es_sfdc_pkce_flow_id', body.flow_id);
            sessionStorage.setItem('es_sfdc_pkce_code_challenge', body.code_challenge);
        } catch (e) {
            const detail = 'sessionStorage write failed (browser storage blocked?): ' + e;
            es.log.warn('[PKCE] ' + detail);
            renderSfdcAuthFailureUi(sfUserName || '(unknown)', detail);
            return;
        }
        let redirect = '/esWebApp/pages/login/salesforce/salesforceOAUTH.jsp?auto=1';
        if (sfLoginUrl) {
            redirect += '&loginUrl=' + encodeURIComponent(sfLoginUrl);
        }
        if (sfUserName) {
            redirect += '&loginHint=' + encodeURIComponent(sfUserName);
        }
        window.location.href = redirect;
    };
    xhr.send();
}

function replaceAPIVersion(signedCanvasRequestJson) {
    // SF returns the version segment in context.links inconsistently - restUrl/sobjectUrl/queryUrl
    // come WITHOUT the 'v' prefix while searchUrl/recentItemsUrl/etc come WITH it. The public REST
    // gateway 404s without the 'v', so we normalize every link to the configured apiVersion.
    const configured = es.salesforceSettings.apiVersion;
    const normalizedVersion = configured.startsWith("v") ? configured : "v" + configured;
    const links = _.mapValues(signedCanvasRequestJson.context.links,
            link => link.replace(/\/services\/data\/v?\d+\.\d+\//, "/services/data/" + normalizedVersion + "/"));

    signedCanvasRequestJson.context.links = links;
    return signedCanvasRequestJson;
}
